<template>
  <div class="view-container">
    <div class="chat-layout">
      <div class="chat-left">
        <div class="panel-header">
          <h4>会话列表</h4>
          <button @click="newChat" class="btn btn-xs btn-primary"><i class="fas fa-plus"></i></button>
        </div>
        <div class="search-box"><input v-model="convSearch" class="form-control" placeholder="搜索会话..."></div>
        <div class="conv-list">
          <div v-for="conv in filteredConvs" :key="conv.id" class="conv-item" :class="{ active: activeConv && activeConv.id === conv.id }" @click="openConversation(conv)">
            <div class="avatar" :style="{ background: conv.avatarColor }">{{ conv.avatar }}</div>
            <div class="conv-info">
              <div class="conv-top">
                <span class="conv-name">{{ conv.name }}</span>
                <span class="conv-time">{{ conv.time }}</span>
              </div>
              <div class="conv-bottom">
                <span class="conv-last">{{ conv.lastMsg }}</span>
                <span v-if="conv.unread > 0" class="unread-badge">{{ conv.unread > 99 ? '99+' : conv.unread }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="chat-right">
        <template v-if="activeConv">
          <div class="chat-header">
            <div class="chat-title">
              <div class="avatar small" :style="{ background: activeConv.avatarColor }">{{ activeConv.avatar }}</div>
              <div>
                <div class="chat-name">{{ activeConv.name }}</div>
                <div class="chat-subtitle">{{ activeConv.isGroup ? activeConv.memberCount + ' 人' : '在线' }}</div>
              </div>
            </div>
            <div class="chat-actions">
              <button class="icon-btn" title="搜索消息"><i class="fas fa-search"></i></button>
              <button class="icon-btn" title="群信息"><i class="fas fa-info-circle"></i></button>
            </div>
          </div>

          <div class="chat-messages" ref="msgContainer">
            <div v-for="(msg, idx) in activeConv.messages" :key="idx" :class="['msg-row', msg.fromMe ? 'msg-me' : 'msg-other']">
              <template v-if="!msg.fromMe">
                <div class="avatar tiny" :style="{ background: activeConv.avatarColor }">{{ activeConv.avatar }}</div>
                <div class="msg-block">
                  <div class="msg-sender" v-if="activeConv.isGroup && idx > 0 && activeConv.messages[idx-1].sender !== msg.sender">{{ msg.sender }}</div>
                  <div class="msg-bubble">{{ msg.text }}</div>
                </div>
              </template>
              <template v-else>
                <div class="msg-block me-block">
                  <div class="msg-bubble me">{{ msg.text }}</div>
                  <div class="msg-status">
                    <span v-if="msg.read" class="read-text"><i class="fas fa-check-double"></i> 已读</span>
                    <span v-else class="unread-text"><i class="fas fa-check"></i> 未读</span>
                  </div>
                </div>
              </template>
            </div>
          </div>

          <div class="chat-input-area">
            <div class="input-tools">
              <button class="icon-btn" title="表情"><i class="fas fa-smile"></i></button>
              <button class="icon-btn" title="图片"><i class="fas fa-image"></i></button>
              <button class="icon-btn" title="文件"><i class="fas fa-paperclip"></i></button>
            </div>
            <div class="input-row">
              <input v-model="newMsg" @keyup.enter="sendMsg" class="form-control msg-input" placeholder="输入消息...">
              <button @click="sendMsg" class="btn btn-primary send-btn">发送</button>
            </div>
          </div>
        </template>
        <div v-else class="empty-chat">
          <i class="fas fa-comments"></i>
          <p>选择一个会话开始聊天</p>
          <p class="hint">支持单聊和群聊</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick } from 'vue'

const convSearch = ref('')
const newMsg = ref('')
const activeConv = ref(null)
const msgContainer = ref(null)

const conversations = ref([
  {
    id: 1, name: '产品研发群', isGroup: true, memberCount: 12, avatar: '研', avatarColor: '#3b82f6',
    time: '刚刚', lastMsg: '王五: 新版本今天可以发布吗？', unread: 5,
    messages: [
      { text: '大家看一下最新的需求文档', sender: '张三', time: '10:00', fromMe: false },
      { text: '收到，正在看', sender: '李四', time: '10:05', fromMe: false },
      { text: '新版本今天可以发布吗？', sender: '王五', time: '10:15', fromMe: false, read: false },
      { text: '测试环境已经通过了', sender: '我', time: '10:18', fromMe: true, read: true },
      { text: '好的，准备发布', sender: '李四', time: '10:20', fromMe: false }
    ]
  },
  {
    id: 2, name: '采购部工作群', isGroup: true, memberCount: 8, avatar: '采', avatarColor: '#10b981',
    time: '10:32', lastMsg: '李四: 供应商合同已确认', unread: 3,
    messages: [
      { text: '供应商A的报价已收到', sender: '赵六', time: '09:00', fromMe: false },
      { text: '比市场价低5%', sender: '赵六', time: '09:02', fromMe: false },
      { text: '供应商合同已确认', sender: '李四', time: '10:30', fromMe: false, read: false },
      { text: '好的，我们这边也确认', sender: '我', time: '10:15', fromMe: true, read: false }
    ]
  },
  {
    id: 3, name: '张三', isGroup: false, avatar: '张', avatarColor: '#8b5cf6',
    time: '昨天', lastMsg: '好的，我稍后回复你', unread: 2,
    messages: [
      { text: '在吗？问个技术问题', sender: '张三', time: '昨天 15:00', fromMe: false, read: false },
      { text: '关于数据库迁移的方案', sender: '张三', time: '昨天 15:02', fromMe: false, read: false },
      { text: '好的，我稍后回复你', sender: '我', time: '昨天 15:10', fromMe: true, read: true }
    ]
  },
  {
    id: 4, name: '王五', isGroup: false, avatar: '王', avatarColor: '#ec4899',
    time: '周二', lastMsg: '[图片]', unread: 0,
    messages: [
      { text: '周五的发布会你去吗？', sender: '王五', time: '周二 09:00', fromMe: false },
      { text: '去的，几点开始？', sender: '我', time: '周二 09:05', fromMe: true, read: true },
      { text: '下午2点，研发楼3层', sender: '王五', time: '周二 09:08', fromMe: false }
    ]
  }
])

const filteredConvs = computed(() => {
  if (!convSearch.value) return conversations.value
  return conversations.value.filter(c => c.name.includes(convSearch.value))
})

const openConversation = (conv) => {
  activeConv.value = conv
  conv.unread = 0
  conv.messages.forEach(m => { if (!m.fromMe) m.read = true })
  nextTick(() => {
    if (msgContainer.value) {
      msgContainer.value.scrollTop = msgContainer.value.scrollHeight
    }
  })
}

const sendMsg = () => {
  const text = newMsg.value.trim()
  if (!text || !activeConv.value) return
  activeConv.value.messages.push({ text, sender: '我', time: '刚刚', fromMe: true, read: false })
  activeConv.value.lastMsg = '我: ' + text
  activeConv.value.time = '刚刚'
  newMsg.value = ''
  nextTick(() => {
    if (msgContainer.value) {
      msgContainer.value.scrollTop = msgContainer.value.scrollHeight
    }
    setTimeout(() => {
      const last = activeConv.value.messages[activeConv.value.messages.length - 1]
      if (last && last.fromMe) last.read = true
    }, 2000)
  })
}

const newChat = () => {
  window.$message.warning('新建会话（选择联系人）')
}
</script>

<style scoped>
.view-container { background: #fff; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.05); overflow: hidden; max-height: calc(100vh - 110px); }
.chat-layout { display: flex; height: calc(100vh - 115px); }

.chat-left { flex: 0 0 300px; border-right: 1px solid #e5e7eb; display: flex; flex-direction: column; }
.panel-header { padding: 12px 16px; border-bottom: 1px solid #e5e7eb; display: flex; justify-content: space-between; align-items: center; }
.panel-header h4 { margin: 0; font-size: 14px; font-weight: 600; color: #1f2937; }
.search-box { padding: 10px 12px; border-bottom: 1px solid #f3f4f6; }
.search-box .form-control { width: 100%; padding: 7px 10px; border: 1px solid #e5e7eb; border-radius: 20px; font-size: 12px; background: #f9fafb; box-sizing: border-box; }
.conv-list { flex: 1; overflow-y: auto; }
.conv-item { display: flex; align-items: center; gap: 12px; padding: 12px 16px; border-bottom: 1px solid #f9fafb; cursor: pointer; transition: background 0.15s; }
.conv-item:hover { background: #f8fafc; }
.conv-item.active { background: #ecfeff; }
.avatar { width: 42px; height: 42px; border-radius: 6px; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 15px; font-weight: 600; flex-shrink: 0; }
.avatar.small { width: 36px; height: 36px; font-size: 13px; }
.avatar.tiny { width: 30px; height: 30px; font-size: 11px; border-radius: 50%; }
.conv-info { flex: 1; min-width: 0; }
.conv-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px; }
.conv-name { font-size: 13px; font-weight: 600; color: #1f2937; }
.conv-time { font-size: 11px; color: #9ca3af; }
.conv-bottom { display: flex; justify-content: space-between; align-items: center; }
.conv-last { font-size: 12px; color: #9ca3af; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 160px; }
.unread-badge { background: #ef4444; color: #fff; font-size: 10px; padding: 2px 6px; border-radius: 10px; min-width: 18px; text-align: center; flex-shrink: 0; }

.chat-right { flex: 1; display: flex; flex-direction: column; min-width: 0; }
.chat-header { padding: 10px 16px; border-bottom: 1px solid #e5e7eb; display: flex; justify-content: space-between; align-items: center; background: #fafbfc; flex-shrink: 0; }
.chat-title { display: flex; align-items: center; gap: 10px; }
.chat-name { font-size: 14px; font-weight: 600; color: #1f2937; }
.chat-subtitle { font-size: 11px; color: #9ca3af; }
.chat-actions { display: flex; gap: 4px; }
.icon-btn { width: 30px; height: 30px; border: none; background: none; border-radius: 4px; cursor: pointer; color: #9ca3af; display: flex; align-items: center; justify-content: center; font-size: 14px; }
.icon-btn:hover { background: #f3f4f6; color: #374151; }

.chat-messages { flex: 1; overflow-y: auto; padding: 16px; display: flex; flex-direction: column; gap: 14px; background: #f5f7fa; }
.msg-row { display: flex; align-items: flex-end; gap: 8px; }
.msg-me { flex-direction: row-reverse; }
.msg-block { max-width: 65%; }
.me-block { display: flex; flex-direction: column; align-items: flex-end; }
.msg-sender { font-size: 11px; color: #6b7280; margin-bottom: 2px; }
.msg-bubble { padding: 10px 14px; border-radius: 14px; font-size: 13px; line-height: 1.5; color: #1f2937; background: #fff; box-shadow: 0 1px 2px rgba(0,0,0,0.06); }
.msg-bubble.me { background: #06b6d4; color: #fff; }
.msg-status { margin-top: 2px; font-size: 10px; }
.read-text { color: #10b981; }
.unread-text { color: #9ca3af; }

.chat-input-area { border-top: 1px solid #e5e7eb; padding: 10px 16px; background: #fff; flex-shrink: 0; }
.input-tools { display: flex; gap: 4px; margin-bottom: 8px; }
.input-row { display: flex; gap: 10px; }
.msg-input { flex: 1; padding: 10px 14px; border: 1px solid #e5e7eb; border-radius: 20px; font-size: 13px; box-sizing: border-box; }
.send-btn { padding: 8px 20px; border-radius: 20px; flex-shrink: 0; }

.empty-chat { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; color: #d1d5db; }
.empty-chat i { font-size: 56px; margin-bottom: 12px; }
.empty-chat p { font-size: 14px; color: #9ca3af; margin-bottom: 4px; }
.empty-chat .hint { font-size: 12px; color: #d1d5db; }

.btn { padding: 6px 14px; border: 1px solid transparent; border-radius: 4px; font-size: 13px; cursor: pointer; display: inline-flex; align-items: center; gap: 4px; transition: all 0.2s; }
.btn-primary { background: #06b6d4; color: #fff; border-color: #06b6d4; }
.btn-primary:hover { background: #0891b2; }
.btn-xs { padding: 3px 8px; font-size: 11px; }
</style>
